import { useMemo } from 'react';
import PropTypes from 'prop-types';

import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Switch } from 'src/@/components/ui/switch';
import { Textarea } from 'src/@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { PopoverVariaveis } from 'src/sections/@dashboard/automacoes/components/PopoverVariaveis';
import { calcularSegmentosSms, removerAcentos } from 'src/utils/smsSegmentos';

import { CAMPANHAS_DESIGN as D } from '../../Campanhas.design';

export default function PassoConteudo({
    form,
    set,
    setVariavelTemplate,
    templates,
    loadingTemplates,
    isWhatsAppBusiness,
    onSelecionarTemplate,
    onFilesChange,
    recording,
    loadingAudio,
    onStartRecording,
    onStopRecording,
}) {
    const ehSms = form.tipoEnvio === 'sms';
    const metricas = useMemo(() => calcularSegmentosSms(form.body), [form.body]);
    const temAcento = ehSms && metricas.encoding === 'UCS-2';

    if (ehSms) {
        return (
            <div className="space-y-4">
                <div className={D.wizard.secao}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <div>
                            <p className={D.wizard.secaoTitulo}>Mensagem</p>
                            <p className={D.wizard.secaoSub}>SMS aceita apenas texto — sem mídia nem formatação.</p>
                        </div>
                        {/* onSelectVariavel entrega o nome sem as chaves. */}
                        <PopoverVariaveis onSelectVariavel={(nome) => set('body', `${form.body}{{${nome}}}`)} />
                    </div>

                    <Textarea
                        value={form.body}
                        onChange={(e) => set('body', e.target.value)}
                        rows={6}
                        placeholder="Digite a mensagem que será enviada..."
                        className={D.wizard.input}
                    />

                    <div className="flex items-center justify-between gap-2 mt-2 text-[11px] tabular-nums">
                        <span className="text-gray-500 dark:text-[#ABABAD]">
                            {`${metricas.caracteres} caracteres · ${metricas.encoding}`}
                        </span>
                        <span className="text-gray-700 dark:text-[#D1D2D3] font-medium">
                            {metricas.segmentos === 0
                                ? 'sem custo'
                                : `${metricas.segmentos} ${metricas.segmentos === 1 ? 'crédito' : 'créditos'} por destinatário`}
                        </span>
                    </div>

                    {temAcento && (
                        <div className={`${D.wizard.avisoAlerta} mt-3`}>
                            <Iconify icon="mdi:alert-outline" width={14} className="flex-shrink-0 mt-px" />
                            <div className="min-w-0 flex-1">
                                <p>
                                    <strong>Acentos dobram o custo.</strong> Caracteres como{' '}
                                    {metricas.caracteresForaGsm7.slice(0, 6).join(' ')} tiram a mensagem do alfabeto
                                    padrão de SMS e reduzem o limite de 160 para 70 caracteres por crédito.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => set('body', removerAcentos(form.body))}
                                    className="mt-1.5 font-semibold underline hover:no-underline"
                                >
                                    Remover acentos do texto
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const templatesAprovados = (templates || []).filter((t) => t.status === 'APPROVED');
    const variaveis = Object.keys(form.templateVariaveis || {}).sort((a, b) => Number(a) - Number(b));

    return (
        <div className="space-y-4">
            {isWhatsAppBusiness && (
                <div className={D.wizard.secao}>
                    <div className="flex items-center justify-between gap-4 mb-3">
                        <div className="min-w-0">
                            <p className={D.wizard.secaoTitulo}>Usar template aprovado</p>
                            <p className={D.wizard.secaoSub}>
                                Obrigatório para iniciar conversa fora da janela de 24h.
                            </p>
                        </div>
                        <Switch checked={form.useTemplate} onCheckedChange={(v) => set('useTemplate', v)} />
                    </div>

                    {form.useTemplate && (
                        <>
                            <Label className={D.wizard.label}>Template</Label>
                            <Select
                                value={form.templateSelecionado?.templateId || 'none'}
                                onValueChange={onSelecionarTemplate}
                            >
                                <SelectTrigger className={D.wizard.input}>
                                    <SelectValue placeholder={loadingTemplates ? 'Carregando...' : 'Selecione'} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Nenhum</SelectItem>
                                    {templatesAprovados.map((t) => (
                                        <SelectItem key={t.templateId || t._id} value={t.templateId || t._id}>
                                            {t.name || t.templateName}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            {form.templateSelecionado && (
                                <div className="mt-3 p-3 rounded-xl bg-gray-50 dark:bg-[#1A1D21] border border-gray-100 dark:border-[#383838]">
                                    <p className="text-[11px] text-gray-600 dark:text-[#ABABAD] whitespace-pre-wrap">
                                        {form.templateSelecionado.conteudo}
                                    </p>
                                </div>
                            )}

                            {variaveis.length > 0 && (
                                <div className="mt-3 space-y-2 p-3 rounded-xl bg-violet-50/50 dark:bg-violet-500/5 border border-violet-100 dark:border-violet-500/20">
                                    <p className={D.wizard.label}>Variáveis do template</p>
                                    {variaveis.map((chave) => (
                                        <div key={chave} className="flex items-center gap-2">
                                            <span className="px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 font-mono text-[10px] flex-shrink-0">
                                                {`{{${chave}}}`}
                                            </span>
                                            <Input
                                                value={form.templateVariaveis[chave] || ''}
                                                onChange={(e) => setVariavelTemplate(chave, e.target.value)}
                                                placeholder="Valor ou {{nome}}"
                                                className={`${D.wizard.input} h-8 text-xs`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            <div className={D.wizard.secao}>
                <div className="flex items-center justify-between gap-2 mb-2">
                    <div>
                        <p className={D.wizard.secaoTitulo}>
                            {form.useTemplate ? 'Mensagem alternativa (opcional)' : 'Mensagem'}
                        </p>
                        <p className={D.wizard.secaoSub}>
                            {form.useTemplate
                                ? 'Usada quando o contato já está dentro da janela de 24h.'
                                : 'Texto que será enviado ao contato.'}
                        </p>
                    </div>
                    <PopoverVariaveis onSelect={(v) => set('body', `${form.body}${v}`)} />
                </div>

                <Textarea
                    value={form.body}
                    onChange={(e) => set('body', e.target.value)}
                    rows={5}
                    placeholder="Digite a mensagem..."
                    className={D.wizard.input}
                />

                {!isWhatsAppBusiness && (
                    <div className={`${D.wizard.avisoAlerta} mt-3`}>
                        <Iconify icon="mdi:alert-outline" width={14} className="flex-shrink-0 mt-px" />
                        <span>
                            Este canal não é oficial. Disparos em massa podem resultar em bloqueio do número.
                        </span>
                    </div>
                )}
            </div>

            <div className={D.wizard.secao}>
                <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <p className={D.wizard.secaoTitulo}>Enviar mídia</p>
                        <p className={D.wizard.secaoSub}>Anexe um arquivo ou grave um áudio.</p>
                    </div>
                    <Switch checked={form.isFile} onCheckedChange={(v) => set('isFile', v)} />
                </div>

                {form.isFile && (
                    <div className="mt-4 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => set('tipoArquivo', 'arquivo')}
                                className={form.tipoArquivo === 'arquivo' ? D.wizard.cardOpcaoAtiva : D.wizard.cardOpcao}
                            >
                                <div className={`${D.wizard.cardIcone} bg-violet-50 dark:bg-violet-500/10 text-[#7C3AED]`}>
                                    <Iconify icon="mdi:paperclip" width={18} />
                                </div>
                                <p className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3]">Arquivo</p>
                            </button>
                            <button
                                type="button"
                                onClick={() => set('tipoArquivo', 'audio')}
                                className={form.tipoArquivo === 'audio' ? D.wizard.cardOpcaoAtiva : D.wizard.cardOpcao}
                            >
                                <div className={`${D.wizard.cardIcone} bg-violet-50 dark:bg-violet-500/10 text-[#7C3AED]`}>
                                    <Iconify icon="mdi:microphone" width={18} />
                                </div>
                                <p className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3]">Áudio</p>
                            </button>
                        </div>

                        {form.tipoArquivo === 'arquivo' && (
                            <div>
                                <Input type="file" multiple onChange={onFilesChange} className={D.wizard.input} />
                                {form.files.length > 0 && (
                                    <p className="text-[11px] text-gray-500 dark:text-[#ABABAD] mt-2">
                                        {`${form.files.length} arquivo(s) selecionado(s)`}
                                    </p>
                                )}
                            </div>
                        )}

                        {form.tipoArquivo === 'audio' && (
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    disabled={loadingAudio}
                                    onClick={recording ? onStopRecording : onStartRecording}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium transition-colors ${
                                        recording
                                            ? 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400'
                                            : 'bg-violet-50 dark:bg-violet-500/10 text-[#7C3AED]'
                                    }`}
                                >
                                    <Iconify icon={recording ? 'mdi:stop' : 'mdi:microphone'} width={16} />
                                    {recording ? 'Parar gravação' : 'Gravar áudio'}
                                </button>
                                {form.audioGravado && (
                                    <span className="text-[11px] text-green-600 dark:text-green-400 flex items-center gap-1">
                                        <Iconify icon="mdi:check-circle-outline" width={14} />
                                        Áudio gravado
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

PassoConteudo.propTypes = {
    form: PropTypes.object.isRequired,
    set: PropTypes.func.isRequired,
    setVariavelTemplate: PropTypes.func.isRequired,
    templates: PropTypes.array,
    loadingTemplates: PropTypes.bool,
    isWhatsAppBusiness: PropTypes.bool,
    onSelecionarTemplate: PropTypes.func.isRequired,
    onFilesChange: PropTypes.func.isRequired,
    recording: PropTypes.bool,
    loadingAudio: PropTypes.bool,
    onStartRecording: PropTypes.func.isRequired,
    onStopRecording: PropTypes.func.isRequired,
};
